import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  createBikeOrderApi,
  deleteBikeOrderApi,
  deleteMealApi,
  getMemberDetailApi,
  transferMealApi,
  updateDocumentStatusApi,
  updateTagApi,
  updateTshirtSizeApi,
} from '../../api/member.api';
import {
  addBikeOrderAct,
  addBikeOrderErrorAct,
  addBikeOrderSuccessAct,
  assignGroupAct,
  assignGroupErrorAct,
  assignGroupSuccessAct,
  deleteBikeOrderAct,
  deleteBikeOrderErrorAct,
  deleteBikeOrderSuccessAct,
  getMemberDetailAct,
  getMemberDetailErrorAct,
  getMemberDetailSuccessAct,
  removeMealAct,
  removeMealErrorAct,
  removeMealSuccessAct,
  transferMealAct,
  transferMealErrorAct,
  transferMealSuccessAct,
  updateDocumentStatusAct,
  updateTagStateAct,
  updateTagStateErrorAct,
  updateTagStateSuccessAct,
  updateTshirtSizeAct,
  updateTshirtSizeErrorAct,
  updateTshirtSizeSuccessAct,
} from './actions';
import { getErrorMessage } from '../../utils/error.util';
import { addMessage } from '../ToastMessagesManager/actions';
import { assignGroupApi } from '../../api/team.api';
import TeamMessages from '../../components/EventTeamDetail/messages';
import messages from '../../components/MemberDetail/messages';

function* fetchMemberDetail({ payload }) {
  try {
    const { data } = yield call(getMemberDetailApi, payload);
    if (data) {
      yield put(getMemberDetailSuccessAct(data));
    } else {
      yield put(getMemberDetailErrorAct({ message: 'Something went wrong' }));
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(addMessage({ message: errMessage, variant: 'error' }));
    yield put(getMemberDetailErrorAct(errMessage));
  }
}

function* updateDocumentStatus({ payload }) {
  const { loadingState, memberId, data, documentId } = payload;
  try {
    if (loadingState === 1) {
      const { data: updatedDocument } = yield call(
        updateDocumentStatusApi,
        memberId,
        data,
      );
      if (updatedDocument) {
        yield all([
          put(
            addMessage({ message: messages.updateMedSuc, variant: 'success' }),
          ),
          put(
            updateDocumentStatusAct(2, memberId, documentId, updatedDocument),
          ),
        ]);
      } else {
        throw new Error('Something went wrong');
      }
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield all(
      put(addMessage({ message: errMessage, variant: 'error' })),
      put(updateDocumentStatusAct(3, memberId, documentId)),
    );
  }
}

function* updateTshirtSize({ payload }) {
  try {
    const { data } = yield call(updateTshirtSizeApi, payload);
    if (data) {
      yield put(
        addMessage({
          message: messages.updateTshirtSuc,
          variant: 'success',
        }),
      );
      yield put(updateTshirtSizeSuccessAct(data));
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(addMessage({ message: errMessage, variant: 'error' }));
    yield put(
      updateTshirtSizeErrorAct({
        error: errMessage,
        oldValue: payload.oldValue,
      }),
    );
  }
}

function* updateTagState({ payload }) {
  const { data: payloadData } = payload;
  try {
    const { data } = yield call(updateTagApi, payload);
    if (data) {
      yield put(updateTagStateSuccessAct(data));
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(addMessage({ message: errMessage, variant: 'error' }));
    yield put(
      updateTagStateErrorAct({
        tagId: payloadData.tagId,
        active: !payloadData.active,
        error: errMessage,
      }),
    );
  }
}

function* transferMeal({ payload }) {
  try {
    const { data } = yield call(transferMealApi, payload);
    if (data) {
      // transfer message
      yield put(
        addMessage({ message: messages.transferMealSuc, variant: 'success' }),
      );
      yield put(transferMealSuccessAct(data));
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(addMessage({ message: errMessage, variant: 'error' }));
    yield put(
      transferMealErrorAct({
        error: errMessage,
      }),
    );
  }
}

function* deleteMeal({ payload }) {
  const { id } = payload;
  try {
    const { data } = yield call(deleteMealApi, id);
    if (data) {
      yield put(
        addMessage({ message: messages.deleteMealSuc, variant: 'success' }),
      );
      yield put(removeMealSuccessAct());
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(addMessage({ message: errMessage, variant: 'error' }));
    yield put(
      removeMealErrorAct({
        error: errMessage,
      }),
    );
  }
}

function* updateAssignedGroup({ payload }) {
  const { metadata, id, data } = payload;
  try {
    const { data: result } = yield call(assignGroupApi, id, data);
    if (result) {
      yield put(assignGroupSuccessAct(result));
      // need to determine the assign or remove removeGrpSuc
      yield put(
        addMessage({ message: TeamMessages.updateGrpSuc, variant: 'success' }),
      );
    } else {
      throw new Error('Something went wrong');
    }
  } catch (error) {
    const errMessage = getErrorMessage(error);
    yield put(addMessage({ message: errMessage, variant: 'error' }));
    yield put(assignGroupErrorAct(metadata));
  }
}

function* addBikeOrder({ payload }) {
  const { id } = payload;
  try {
    const { data } = yield call(createBikeOrderApi, id);
    yield put(addBikeOrderSuccessAct(data));
    yield put(
      addMessage({ message: messages.addBikeOrderSuc, variant: 'success' }),
    );
  } catch (error) {
    yield put(
      addMessage({ message: getErrorMessage(error), variant: 'error' }),
    );
    yield put(addBikeOrderErrorAct());
  }
}

function* deleteBikeOrder({ payload }) {
  const { memberId, orderId } = payload;
  try {
    yield call(deleteBikeOrderApi, memberId, orderId);
    yield put(deleteBikeOrderSuccessAct({ orderId }));
    yield put(
      addMessage({ message: messages.deleteBikeOrderSuc, variant: 'success' }),
    );
  } catch (error) {
    yield put(
      addMessage({ message: getErrorMessage(error), variant: 'error' }),
    );
    yield put(deleteBikeOrderErrorAct());
  }
}

// Individual exports for testing
export default function* memberDetailSaga() {
  //  uncomment this to setp default action
  yield takeLatest(getMemberDetailAct.toString(), fetchMemberDetail);
  yield takeLatest(updateDocumentStatusAct.toString(), updateDocumentStatus);
  yield takeLatest(updateTshirtSizeAct.toString(), updateTshirtSize);
  yield takeEvery(updateTagStateAct.toString(), updateTagState);
  yield takeLatest(transferMealAct.toString(), transferMeal);
  yield takeLatest(removeMealAct.toString(), deleteMeal);
  yield takeEvery(assignGroupAct.toString(), updateAssignedGroup);
  yield takeLatest(addBikeOrderAct.toString(), addBikeOrder);
  yield takeLatest(deleteBikeOrderAct.toString(), deleteBikeOrder);
}
